import { useSyncExternalStore } from 'react';

import { cn } from '@mint-lib/tw-utils';
import type { GridApi } from 'ag-grid-enterprise';

import { IconButton } from './IconButton.jsx';
import { MainButton } from './MainButton.jsx';
import { SearchBar } from './SearchBar.jsx';
import { SecondaryButton } from './SecondaryButton.jsx';
import { SelectedRowsCounter } from './SelectedRowsCounter.jsx';
import { Separator } from './Separator.jsx';
import { ToggleButtons } from './ToggleButtons.jsx';
import { ToolbarSwitch } from './ToolbarSwitch.jsx';

type Visibility = 'static' | 'dynamic' | 'both';

type AGGridToolbarProps = Readonly<{
  className?: string;
  gridApi: GridApi | null;
  mainButton?: Array<{
    visibility: Visibility;
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    color?: 'primary' | 'ai';
    disabled?: boolean;
    separator?: boolean;
    ariaLabel?: string;
    dataDdActionName?: string;
  }>;
  secondaryButton?: Array<{
    visibility: Visibility;
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    disabled?: boolean;
    separator?: boolean;
  }>;
  iconButton?: Array<{
    visibility: Visibility;
    onClick: () => void;
    icon?: React.ReactNode;
    color?: 'primary' | 'ai' | 'warning' | 'error';
    disabled?: boolean;
    separator?: boolean;
    tooltip?: React.ReactNode;
    ariaLabel?: string;
    dataDdActionName?: string;
  }>;
  toolbarSwitch?: {
    visibility: Visibility;
    label: string;
    disabled?: boolean;
    size?: 'small' | 'medium';
    separator?: boolean;
    queryParamKey: string;
  };
  searchBar?: {
    visibility: Visibility;
    placeholder?: string;
    disabled?: boolean;
    separator?: boolean;
    queryParamKey: string;
  };
  toggleButtons?: {
    visibility: Visibility;
    values: string[];
    defaultValue?: string;
    separator?: boolean;
    queryParamKey: string;
  };
  customComponent?: React.ReactNode;
}>;

const shouldShowItem = (visibility: Visibility, isRowSelected: boolean) => {
  if (visibility === 'both') return true;
  if (visibility === 'dynamic') return isRowSelected;
  if (visibility === 'static') return !isRowSelected;
  return false;
};

export function AGGridToolbar({
  className,
  gridApi,
  mainButton,
  secondaryButton,
  searchBar,
  iconButton,
  toggleButtons,
  toolbarSwitch,
  customComponent,
}: AGGridToolbarProps) {
  const isRowSelected = useSyncExternalStore(
    // selectionChanged fires once per selection change, also for rows deselected off screen (rowSelected only fires for rendered rows).
    (callback) => {
      gridApi?.addEventListener('selectionChanged', callback);
      return () => gridApi?.removeEventListener('selectionChanged', callback);
    },
    () => (gridApi?.getSelectedNodes()?.length ?? 0) > 0,
  );

  return (
    <nav
      data-state={isRowSelected ? 'dynamic' : 'static'}
      className={cn(
        'mb-2 flex items-center gap-2 rounded-lg p-2 data-[state=dynamic]:box-border data-[state=dynamic]:border data-[state=dynamic]:border-action-focusBorder data-[state=dynamic]:bg-ui-primary data-[state=static]:overflow-hidden data-[state=static]:border data-[state=static]:border-transparent-main data-[state=static]:bg-transparent-main',
        className,
      )}
    >
      {isRowSelected && (
        <div className="flex items-center gap-2">
          <SelectedRowsCounter gridApi={gridApi} />
          <Separator />
        </div>
      )}
      {mainButton?.map(
        (btn, i) =>
          shouldShowItem(btn.visibility, isRowSelected) && (
            <MainButton
              key={i}
              label={btn.label}
              onClick={btn.onClick}
              icon={btn.icon}
              color={btn.color}
              disabled={btn.disabled}
              separator={btn.separator}
              ariaLabel={btn.ariaLabel}
              dataDdActionName={btn.dataDdActionName}
            />
          ),
      )}
      {secondaryButton?.map(
        (btn, i) =>
          shouldShowItem(btn.visibility, isRowSelected) && (
            <SecondaryButton
              key={i}
              label={btn.label}
              onClick={btn.onClick}
              icon={btn.icon}
              disabled={btn.disabled}
              separator={btn.separator}
            />
          ),
      )}
      {iconButton?.map(
        (btn, i) =>
          shouldShowItem(btn.visibility, isRowSelected) && (
            <IconButton
              key={i}
              onClick={btn.onClick}
              icon={btn.icon}
              color={btn.color}
              disabled={btn.disabled}
              separator={btn.separator}
              tooltip={btn.tooltip}
              ariaLabel={btn.ariaLabel}
              dataDdActionName={btn.dataDdActionName}
            />
          ),
      )}
      {toolbarSwitch &&
        shouldShowItem(toolbarSwitch.visibility, isRowSelected) && (
          <ToolbarSwitch
            label={toolbarSwitch.label}
            disabled={toolbarSwitch.disabled}
            size={toolbarSwitch.size}
            separator={toolbarSwitch.separator}
            queryParamKey={toolbarSwitch.queryParamKey}
          />
        )}
      {customComponent}
      <div className="flex flex-1 items-center justify-end gap-2">
        {toggleButtons &&
          shouldShowItem(toggleButtons.visibility, isRowSelected) && (
            <ToggleButtons
              values={toggleButtons.values}
              defaultValue={toggleButtons.defaultValue}
              separator={toggleButtons.separator}
              queryParamKey={toggleButtons.queryParamKey}
            />
          )}
        {searchBar && shouldShowItem(searchBar.visibility, isRowSelected) && (
          <SearchBar
            placeholder={searchBar.placeholder}
            disabled={searchBar.disabled}
            separator={searchBar.separator}
            queryParamKey={searchBar.queryParamKey}
          />
        )}
      </div>
    </nav>
  );
}
